import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { baseGridDefaults } from './gridDefaults';
import type { GridConfig } from './gridConfigFactory';
import type { GridQueryInput } from './gridUtils';
import type { QueryResult } from '@flowqa/shared';
import type { SxProps, Theme } from '@mui/material/styles';
import type { QueryKey } from '@tanstack/react-query';

type TableGridOverridesWithConfig<TDto> = {
  gridConfig: GridConfig<TDto>;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<TDto>>;
  queryKey?: QueryKey;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  interactionMode?: TableGridInteractionMode;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  noRowsText?: string;
  onRowsChange?: (rows: TDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  showToolbar?: boolean;
  chrome?: TableGridChromePreset;
};

type TableGridOverridesNoConfig<TDto> = {
  gridConfig?: undefined;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<TDto>>;
  queryKey: QueryKey;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  interactionMode?: TableGridInteractionMode;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  noRowsText?: string;
  onRowsChange?: (rows: TDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  showToolbar?: boolean;
  chrome?: TableGridChromePreset;
};

type TableGridOverrides<TDto> =
  | TableGridOverridesWithConfig<TDto>
  | TableGridOverridesNoConfig<TDto>;

type TableGridProps<TDto> = {
  queryFn: (input: GridQueryInput) => Promise<QueryResult<TDto>>;
  queryKey: QueryKey;
  pageSizeOptions: number[];
  initialPageSize?: number;
  interactionMode: TableGridInteractionMode;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  noRowsText: string;
  onRowsChange?: (rows: TDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  showToolbar: boolean;
  autoHeight: true;
  skeletonGetRowHeight: null;
  containerSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
};

type TableGridChromePreset = 'default' | 'embedded';
type TableGridInteractionMode = 'interactive' | 'static';

type TableGridChrome = { containerSx?: SxProps<Theme>; sx?: SxProps<Theme> };

const resolveTableGridChrome = (preset?: TableGridChromePreset): TableGridChrome => {
  if (preset !== 'embedded') return {};
  return {};
};

const resolveTableGridInteraction = ({
  interactionMode,
}: {
  interactionMode?: TableGridInteractionMode;
}): TableGridInteractionMode => {
  const resolvedInteractionMode = interactionMode ?? 'interactive';
  return resolvedInteractionMode;
};

export const buildTableGridProps = <TDto>({
  gridConfig,
  queryFn,
  queryKey,
  pageSizeOptions,
  initialPageSize,
  interactionMode,
  disablePageSizeSelect,
  hideFooter,
  noRowsText,
  onRowsChange,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  showToolbar,
  chrome,
}: TableGridOverrides<TDto>): TableGridProps<TDto> => {
  const resolvedQueryFn = queryFn ?? gridConfig?.queryFn;
  const resolvedQueryKey = queryKey ?? gridConfig?.queryKey;
  const resolvedPageSizeOptions =
    pageSizeOptions ?? gridConfig?.pageSizeOptions ?? baseGridDefaults.pageSizeOptions;
  const resolvedNoRowsText =
    noRowsText ?? gridConfig?.noRowsText ?? getEmptyStateMessage('data.noResultsFound');
  if (!resolvedQueryFn || !resolvedQueryKey) {
    throw new Error('buildTableGridProps requires queryFn and queryKey');
  }
  const resolvedInteractionMode = resolveTableGridInteraction({
    interactionMode,
  });

  return {
    queryFn: resolvedQueryFn,
    queryKey: resolvedQueryKey,
    pageSizeOptions: resolvedPageSizeOptions,
    initialPageSize,
    disablePageSizeSelect,
    hideFooter,
    noRowsText: resolvedNoRowsText,
    onRowsChange,
    onQueryInput,
    onDataUpdatedAt,
    showLastUpdated,
    ...resolveTableGridChrome(chrome),
    showToolbar: showToolbar ?? false,
    interactionMode: resolvedInteractionMode,
    autoHeight: true,
    skeletonGetRowHeight: null,
  };
};
